// "https:", "javascript:", "mailto:" ...: anything with a scheme leaves the app.
const URL_SCHEME = /^[a-z][a-z0-9+.-]*:/i

/**
 * Return `raw` as a path inside this app, otherwise null.
 *
 * `destUrl` arrives from the query string, so anyone can craft it. Only a
 * path in this app is honoured. "/templates?x=1" is kept as is. A relative
 * path gets its leading slash: production's approval email links to
 * `comms.bhanzu.com?destUrl=approve-template/<id>/email`, written for the app
 * that owned the root before V2, and it must still open the approval page.
 * Anything with a URL scheme ("https:", "javascript:"), protocol-relative
 * "//host" and backslash forms, and the literal string "null" (what the old
 * builder produced when there was no destination) are dropped, so the
 * redirect after login can never leave the app.
 *
 * @param {string|null|undefined} raw  the decoded `destUrl` value
 * @returns {string|null}
 */
export const safeDestination = (raw) => {
    if (typeof raw !== 'string' || !raw || raw === 'null') return null
    if (URL_SCHEME.test(raw) || raw.startsWith('\\')) return null
    const path = raw.startsWith('/') ? raw : `/${raw}`
    if (path.startsWith('//') || path.startsWith('/\\')) return null
    return path
}

/**
 * Router path of the sign-in screen for a signed-out visitor at
 * `pathname` + `search`, remembering where they were as `destUrl`.
 *
 * A destUrl the visitor already carries is kept rather than nested inside a
 * new one, and the home page is not worth remembering. Paths are
 * router-relative (no basename).
 *
 * @param {string} pathname  router pathname, e.g. "/templates"
 * @param {string} [search]  query string including "?", or ""
 * @returns {string} "/login" or "/login?destUrl=<encoded path>"
 */
export const loginRoute = (pathname, search = '') => {
    const carried = safeDestination(new URLSearchParams(search).get('destUrl'))
    const here = `${pathname}${search}`
    const remember = carried || (here === '/' ? null : here)
    return remember
        ? `/login?destUrl=${encodeURIComponent(remember)}`
        : '/login'
}

/**
 * Build the admin-auth login URL that returns to this app after Google auth.
 *
 * admin-auth reads `return_url` from the query string, carries it through
 * the OAuth `state`, and 302s to it once the cookie is set. So `return_url`
 * must be the app's own root on the current host, base path included. The
 * app is served at the host root (empty base path) since 2026-10-12; it was
 * under `/v2` before, where a bare origin would have landed on another app.
 *
 * Both levels are URL-encoded, so a destination carrying its own query
 * string survives the round trip.
 *
 * @param {object} args
 * @param {string} args.baseURL   API origin, e.g. "https://api.bhanzu.com"
 * @param {string} args.origin    page origin, e.g. "https://comms.bhanzu.com"
 * @param {string} args.basePath  CRA PUBLIC_URL: "" at a host root, or a prefix such as "/v2"
 * @param {string|null} [args.destUrl]  in-app path to land on after login
 * @returns {string}
 */
export const buildLoginUrl = ({ baseURL, origin, basePath, destUrl }) => {
    const appRoot = `${origin}${(basePath || '').replace(/\/+$/, '')}/`
    const destination = safeDestination(destUrl)
    const returnUrl = destination
        ? `${appRoot}?destUrl=${encodeURIComponent(destination)}`
        : appRoot
    return `${baseURL}/admin-auth/v1/login?return_url=${encodeURIComponent(
        returnUrl
    )}`
}
